import React, { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { DateRange } from "react-date-range";
import { Box, Chip, Divider, Stack, Typography, useTheme } from "@mui/material";
import { v4 as uuid } from "uuid";
import { startOfMonth, isBefore, isSameMonth } from "date-fns";

import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import datePickerStyles from "./DateRangeSelector.module.css";
import {
  getWeekendsRange,
  getWeekdaysRange,
  groupDatesIntoWeekends,
  groupDatesIntoWeeks,
  DateRangeProps,
  getAllDatesFromPeriod,
  formatDatesToStrings,
  findEarliestEventDate,
  findLatestEventDate,
} from "./utils";
import { updateDatesSelection } from "@/global/store/data/gameFilters";
import { useAppStore } from "@/global/store";
import { selectEvents } from "@/global/store/data/purchaseSeats/events";

interface Range {
  id: string;
  name: string;
  uid: string;
  isSelected: boolean;
}

const dateFilterTypes: Range[] = [
  {
    id: uuid(),
    name: "Any Day",
    uid: "any_day",
    isSelected: true,
  },
  {
    id: uuid(),
    name: "Weekends",
    uid: "weekends",
    isSelected: false,
  },
  {
    id: uuid(),
    name: "Weekdays",
    uid: "weekdays",
    isSelected: false,
  },
];

const defaultDateRange: DateRangeProps = {
  startDate: new Date(),
  endDate: new Date(),
  //key: "selection",
};

export function DateRangeSelector(props: { title?: string }) {
  const theme = useTheme();
  const dispatch = useDispatch();
  const isEarliestDateCalculated = useRef(false);

  const [dateTypes, setDateTypes] = useState(dateFilterTypes);
  const [currentDate, setCurrentDate] = useState(new Date());

  const [dateRanges, setDateRanges] = useState([defaultDateRange]);
  const [datesList, setDatesList] = useState<string[]>([]);
  const events = useAppStore(selectEvents);

  const handleDateRangeSelect = (range: any) => {
    const values = Object.values(range);
    const dateRange = [values[0]] as DateRangeProps[];
    setDatesList([]);
    setDatesList(getAllDatesFromPeriod(dateRange));
    setDateRanges(dateRange);
    let newDateTypes = dateTypes.map((itm) => ({
      ...itm,
      isSelected: itm.uid === "any_day",
    }));
    setDateTypes(newDateTypes);
  };

  const handleSelectDateType = (selection: Range) => {
    let newDateTypes = dateTypes.map((itm) => ({
      ...itm,
      isSelected: itm.uid === selection.uid,
    }));
    setDateTypes(newDateTypes);

    const latestDate = findLatestEventDate(events);

    if (selection.uid === "weekends") {
      const weekendDaysRange = getWeekendsRange(currentDate, latestDate);
      const listOfDates = formatDatesToStrings(weekendDaysRange);
      setDatesList(listOfDates);
      setDateRanges(groupDatesIntoWeekends(weekendDaysRange));
    } else if (selection.uid === "weekdays") {
      const weekDaysRange = getWeekdaysRange(currentDate, latestDate);
      const listOfDates = formatDatesToStrings(weekDaysRange);
      setDatesList(listOfDates);
      setDateRanges(groupDatesIntoWeeks(weekDaysRange));
    } else {
      setDatesList([]);
      setDateRanges([{ startDate: currentDate, endDate: currentDate }]);
    }
  };

  const handleChangeView = (newDateView: Date) => {
    const selectedDate = newDateView;
    const currentDate = new Date();

    if (
      isSameMonth(selectedDate, currentDate) ||
      isBefore(selectedDate, currentDate)
    ) {
      setCurrentDate(currentDate);
    } else {
      setCurrentDate(startOfMonth(selectedDate));
    }
  };

  useEffect(() => {
    dispatch(updateDatesSelection(datesList));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [datesList]);

  React.useEffect(() => {
    if (events.length > 0 && !isEarliestDateCalculated.current) {
      const earliestDate = findEarliestEventDate(events);
      setCurrentDate(earliestDate);
      setDateRanges([{ startDate: earliestDate, endDate: earliestDate }]);
      isEarliestDateCalculated.current = true;
    }
  }, [events]);

  return (
    <Box>
      {props?.title ? (
        <Box>
          <Typography
            variant="subtitle1"
            fontWeight={"700"}
            color="text.primary"
          >
            {props?.title}
          </Typography>
        </Box>
      ) : null}

      <Stack mt={2} direction={"row"} spacing={1}>
        {dateTypes.map((itm) => {
          return (
            <Chip
              key={uuid()}
              label={itm.name}
              variant={itm.isSelected ? "filled" : "outlined"}
              color={itm.isSelected ? "primary" : "default"}
              clickable
              onClick={() => handleSelectDateType(itm)}
            />
          );
        })}
      </Stack>
      <Box mt={2}>
        <Divider />
      </Box>
      <Box
        mt={2}
        border={`1px solid ${theme.palette.divider}`}
        borderRadius={0.8}
        p={2}
        pt={0}
      >
        <DateRange
          onChange={(item) => handleDateRangeSelect(item)}
          moveRangeOnFirstSelection={false}
          ranges={dateRanges}
          onShownDateChange={handleChangeView}
          classNames={{ ...datePickerStyles }}
        />
      </Box>
    </Box>
  );
}
